import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sparkles, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import Spinner from "../components/Spinner";

const PROFILE_TYPES = [
  { v: "artist", label: "Artiste" },
  { v: "influencer", label: "Influenceur / Influenceuse" },
  { v: "promoter", label: "Promoteur" },
  { v: "journalist", label: "Journaliste" },
  { v: "creator", label: "Créateur / Créatrice de contenu" },
  { v: "other", label: "Autre" },
];

export default function BecomeAmbassadorPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: user?.full_name || "",
    email: user?.email || "",
    phone: "",
    social_links: "",
    profile_type: "influencer",
    motivation: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      await api.post("/ambassador-applications", form);
      setDone(true);
      toast.success("Demande envoyée avec succès");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="je-container py-16 max-w-2xl" data-testid="ambassador-application-success">
        <div className="bg-white border rounded-2xl p-8 text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center">
            <CheckCircle2 className="w-9 h-9 text-green-600" />
          </div>
          <h1 className="text-2xl md:text-3xl font-extrabold mt-4" style={{ fontFamily: "Outfit" }}>Demande envoyée !</h1>
          <p className="text-gray-600 mt-2">
            Nous avons bien reçu votre candidature pour devenir ambassadeur JohnElektwo.
            Notre équipe étudie votre dossier et reviendra vers vous par email.
          </p>
          <p className="text-xs text-gray-400 mt-4">Vous pourrez suivre le statut de votre demande depuis votre compte → onglet Ambassadeur.</p>
          <div className="flex gap-2 justify-center mt-6">
            <button onClick={() => nav(user ? "/account" : "/login")} className="je-btn-primary">
              {user ? "Voir mon compte" : "Me connecter"}
            </button>
            <Link to="/" className="border border-gray-300 rounded-lg px-4 py-2 font-semibold">Retour à l'accueil</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="je-container py-10 max-w-2xl" data-testid="become-ambassador-page">
      <div className="bg-gradient-to-br from-[#0052CC] to-[#001A5C] text-white rounded-2xl p-6 md:p-8 mb-6">
        <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest">
          <Sparkles className="w-4 h-4" /> Programme Ambassadeur
        </div>
        <h1 className="text-3xl md:text-4xl font-extrabold mt-3" style={{ fontFamily: "Outfit" }}>
          Devenez ambassadeur JohnElektwo
        </h1>
        <p className="opacity-90 mt-2 text-sm md:text-base">
          Partagez votre passion et gagnez une commission sur chaque commande générée par votre lien unique.
          Idéal pour les artistes, influenceurs, promoteurs, journalistes et créateurs de contenu.
        </p>
      </div>

      <form onSubmit={submit} className="bg-white border rounded-2xl p-5 md:p-6 shadow-sm space-y-4" data-testid="ambassador-application-form">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase font-semibold text-gray-500">Nom et prénom *</label>
            <input required minLength={2} maxLength={150} value={form.name} onChange={upd("name")}
              placeholder="Ex: Marie Louis" className="w-full border-2 border-gray-200 rounded-lg px-3 py-2.5 mt-1 focus:border-[#0052CC] focus:outline-none transition" data-testid="app-name" />
          </div>
          <div>
            <label className="text-xs uppercase font-semibold text-gray-500">Email *</label>
            <input required type="email" maxLength={200} value={form.email} onChange={upd("email")}
              placeholder="votre@email.com" className="w-full border-2 border-gray-200 rounded-lg px-3 py-2.5 mt-1 focus:border-[#0052CC] focus:outline-none transition" data-testid="app-email" />
          </div>
          <div>
            <label className="text-xs uppercase font-semibold text-gray-500">Téléphone / WhatsApp *</label>
            <input required minLength={5} value={form.phone} onChange={upd("phone")}
              placeholder="+509 3XXX XXXX" className="w-full border-2 border-gray-200 rounded-lg px-3 py-2.5 mt-1 focus:border-[#0052CC] focus:outline-none transition" data-testid="app-phone" />
          </div>
          <div>
            <label className="text-xs uppercase font-semibold text-gray-500">Type de profil *</label>
            <select value={form.profile_type} onChange={upd("profile_type")}
              className="w-full border-2 border-gray-200 rounded-lg px-3 py-2.5 mt-1 focus:border-[#0052CC] focus:outline-none transition bg-white" data-testid="app-profile-type">
              {PROFILE_TYPES.map((p) => <option key={p.v} value={p.v}>{p.label}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="text-xs uppercase font-semibold text-gray-500">Liens vers vos réseaux sociaux</label>
          <textarea rows={3} maxLength={800} value={form.social_links} onChange={upd("social_links")}
            placeholder="Instagram: https://…&#10;Facebook: https://…&#10;TikTok: https://…&#10;YouTube: https://…"
            className="w-full border-2 border-gray-200 rounded-lg px-3 py-2.5 mt-1 focus:border-[#0052CC] focus:outline-none transition resize-y font-mono text-sm"
            data-testid="app-social" />
          <p className="text-[11px] text-gray-400 mt-1">Ajoutez tous vos réseaux principaux (un par ligne).</p>
        </div>
        <div>
          <label className="text-xs uppercase font-semibold text-gray-500">Motivation (facultatif)</label>
          <textarea rows={3} maxLength={2000} value={form.motivation} onChange={upd("motivation")}
            placeholder="Pourquoi souhaitez-vous devenir ambassadeur JohnElektwo ?"
            className="w-full border-2 border-gray-200 rounded-lg px-3 py-2.5 mt-1 focus:border-[#0052CC] focus:outline-none transition resize-y" data-testid="app-motivation" />
        </div>
        <button type="submit" disabled={loading}
          className="je-btn-primary w-full md:w-auto inline-flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="app-submit">
          {loading && <Spinner className="w-4 h-4" />}
          {loading ? "Envoi…" : "Envoyer ma candidature"}
        </button>
        <p className="text-[11px] text-gray-400">
          Vos données ne seront utilisées que pour étudier votre candidature.
          Vous recevrez un email dès que l'équipe aura examiné votre dossier.
        </p>
      </form>
    </div>
  );
}
